import { useCallback, useEffect, useMemo, useState } from 'react';
import { BookOpen, Loader2, Lock, LockOpen, Plus, TriangleAlert } from 'lucide-react';
import { RequireRole } from '../components/RequireRole';
import { inputClass } from '../components/fields';
import {
  createEntry,
  editEntry,
  formatOre,
  getLedger,
  listYears,
  openYear,
  closeYear,
  reopenYear,
  parseSekToOre,
  setBudget,
  voidEntry,
  type Direction,
  type FinancialYear,
  type LedgerView,
} from '../lib/finance';
import type { Me } from '../lib/session';

type Load = 'loading' | 'ready' | 'error';
type Notice = { tone: 'success' | 'error'; text: string } | null;

const EMPTY_FORM = {
  amount: '',
  direction: 'income' as Direction,
  entryDate: '',
  category: '',
  description: '',
  isReimbursement: false,
};

/**
 * Spec 006 — the open books (§19, SC-555..576). Any member sees the ledger, running balance, per-category
 * totals and budget-vs-actual, read-only. The board (treasurer) additionally records/edits/voids entries,
 * sets the budget, and opens/closes/reopens financial years. Four states: loading → ready → empty → error.
 */
function FinancesInner({ user }: { user: Me }) {
  const isBoard = user.role === 'board' || user.role === 'admin';

  const [load, setLoad] = useState<Load>('loading');
  const [years, setYears] = useState<FinancialYear[]>([]);
  const [selectedYear, setSelectedYear] = useState<number | null>(null);
  const [view, setView] = useState<LedgerView | null>(null);
  const [notice, setNotice] = useState<Notice>(null);
  const [busy, setBusy] = useState(false);

  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState({ ...EMPTY_FORM });

  const currentYearRow = useMemo(
    () => years.find((y) => y.year === selectedYear) ?? null,
    [years, selectedYear],
  );
  const yearClosed = currentYearRow?.status === 'closed';

  const loadYears = useCallback(async () => {
    const r = await listYears();
    if (r.kind !== 'ok') {
      setLoad('error');
      return;
    }
    setYears(r.data);
    setSelectedYear((prev) => prev ?? r.data[0]?.year ?? null);
  }, []);

  const loadLedger = useCallback(async (year: number) => {
    const r = await getLedger(year);
    if (r.kind === 'ok') {
      setView(r.data);
      setLoad('ready');
    } else if (r.kind === 'notfound') {
      setView(null);
      setLoad('ready');
    } else {
      setLoad('error');
    }
  }, []);

  useEffect(() => {
    void loadYears();
  }, [loadYears]);

  useEffect(() => {
    if (selectedYear != null) void loadLedger(selectedYear);
  }, [selectedYear, loadLedger]);

  async function refresh() {
    await loadYears();
    if (selectedYear != null) await loadLedger(selectedYear);
  }

  function beginCreate() {
    setEditingId(null);
    setForm({ ...EMPTY_FORM });
    setNotice(null);
    setShowForm(true);
  }

  function beginEdit(entryId: string) {
    const e = view?.entries.find((x) => x.id === entryId);
    if (!e) return;
    setEditingId(entryId);
    setForm({
      amount: (e.amountOre / 100).toFixed(2).replace('.', ','),
      direction: e.direction,
      entryDate: e.entryDate.slice(0, 10),
      category: e.category,
      description: e.description,
      isReimbursement: e.isReimbursement,
    });
    setNotice(null);
    setShowForm(true);
  }

  async function submitEntry() {
    if (busy || selectedYear == null) return;
    const amountOre = parseSekToOre(form.amount);
    if (amountOre == null || amountOre < 1) {
      setNotice({ tone: 'error', text: 'Enter an amount in euro, e.g. 25 or 25,00.' });
      return;
    }
    if (!form.entryDate) {
      setNotice({ tone: 'error', text: 'Choose a date within the financial year.' });
      return;
    }
    if (!form.category.trim() || !form.description.trim()) {
      setNotice({ tone: 'error', text: 'Category and description are required.' });
      return;
    }
    setBusy(true);
    setNotice(null);
    const entryDate = new Date(`${form.entryDate}T12:00:00.000Z`).toISOString();
    const payload = {
      amountOre,
      direction: form.direction,
      entryDate,
      category: form.category.trim(),
      description: form.description.trim(),
      isReimbursement: form.isReimbursement,
    };
    const r = editingId
      ? await editEntry(editingId, payload)
      : await createEntry({ year: selectedYear, ...payload });
    setBusy(false);
    if (r.kind === 'ok') {
      setShowForm(false);
      setEditingId(null);
      setForm({ ...EMPTY_FORM });
      setNotice({ tone: 'success', text: editingId ? 'Entry updated.' : 'Entry recorded.' });
      void refresh();
    } else if (r.kind === 'invalid') {
      setNotice({ tone: 'error', text: r.message ?? 'Please check the entry details.' });
    } else if (r.kind === 'conflict') {
      setNotice({ tone: 'error', text: r.message });
    } else if (r.kind === 'forbidden') {
      setNotice({ tone: 'error', text: 'Only the board can change the books.' });
    } else {
      setNotice({ tone: 'error', text: 'We couldn’t save the entry. Please try again.' });
    }
  }

  async function onVoid(entryId: string) {
    if (busy) return;
    const reason = window.prompt('Reason for voiding this entry (kept in the audit trail):');
    if (reason == null) return;
    if (!reason.trim()) {
      setNotice({ tone: 'error', text: 'A reason is required to void an entry.' });
      return;
    }
    setBusy(true);
    const r = await voidEntry(entryId, reason.trim());
    setBusy(false);
    if (r.kind === 'ok') {
      setNotice({ tone: 'success', text: 'Entry voided. It stays in the books, struck through.' });
      void refresh();
    } else if (r.kind === 'conflict') {
      setNotice({ tone: 'error', text: r.message });
    } else {
      setNotice({ tone: 'error', text: 'We couldn’t void the entry. Please try again.' });
    }
  }

  async function onToggleYear() {
    if (busy || !currentYearRow) return;
    setBusy(true);
    const r = yearClosed ? await reopenYear(currentYearRow.id) : await closeYear(currentYearRow.id);
    setBusy(false);
    if (r.kind === 'ok') {
      setNotice({ tone: 'success', text: yearClosed ? 'Financial year reopened.' : 'Financial year closed.' });
      void refresh();
    } else {
      setNotice({ tone: 'error', text: r.kind === 'conflict' ? r.message : 'We couldn’t change the year.' });
    }
  }

  async function onOpenNextYear() {
    if (busy) return;
    const maxYear = years.reduce((m, y) => Math.max(m, y.year), new Date().getUTCFullYear());
    setBusy(true);
    const r = await openYear(maxYear + 1);
    setBusy(false);
    if (r.kind === 'ok') {
      setSelectedYear(r.data.year);
      setNotice({ tone: 'success', text: `Opened financial year ${r.data.year}.` });
      void refresh();
    } else {
      setNotice({ tone: 'error', text: r.kind === 'conflict' ? r.message : 'We couldn’t open the next year.' });
    }
  }

  if (load === 'loading') {
    return (
      <div className="mx-auto flex w-full max-w-md flex-col items-center px-6 py-24" aria-live="polite">
        <Loader2 aria-hidden className="h-8 w-8 animate-spin text-accent" />
        <p className="mt-4 font-serif text-lg text-ink">Loading the books…</p>
      </div>
    );
  }

  if (load === 'error') {
    return (
      <section className="mx-auto w-full max-w-2xl px-4 py-24">
        <p role="alert" className="flex items-start gap-2 rounded-md border border-error/40 bg-error/5 px-3 py-2 font-sans text-sm text-error">
          <TriangleAlert aria-hidden className="mt-0.5 h-4 w-4 shrink-0" />
          We couldn’t load the finances. Please refresh and try again.
        </p>
      </section>
    );
  }

  return (
    <section className="mx-auto max-w-5xl px-4 py-12">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="font-display text-xs uppercase tracking-[0.2em] text-accent-deep">Open finances</p>
          <h1 className="mt-1 flex items-center gap-2 font-display text-3xl font-semibold uppercase tracking-[0.08em] text-ink">
            <BookOpen aria-hidden className="accent-glow h-7 w-7 text-accent" /> The books
          </h1>
          <p className="mt-2 font-serif text-base text-muted">
            Every euro in and out, open to every member (§19). Amounts in EUR.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <label htmlFor="year" className="font-sans text-sm text-muted">
            Year
          </label>
          <select
            id="year"
            value={selectedYear ?? ''}
            onChange={(e) => setSelectedYear(Number(e.target.value))}
            className={`${inputClass} w-auto`}
          >
            {years.map((y) => (
              <option key={y.id} value={y.year}>
                {y.year}
                {y.status === 'closed' ? ' (closed)' : ''}
              </option>
            ))}
          </select>
          {isBoard && (
            <button
              type="button"
              onClick={beginCreate}
              disabled={yearClosed}
              className="inline-flex items-center gap-2 rounded-md bg-accent px-3.5 py-2 font-sans text-sm font-semibold text-bg transition-colors duration-150 hover:bg-accent-deep focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent disabled:opacity-50"
              title={yearClosed ? 'This year is closed' : undefined}
            >
              <Plus aria-hidden className="h-4 w-4" /> New entry
            </button>
          )}
        </div>
      </header>

      {notice && (
        <p
          role={notice.tone === 'error' ? 'alert' : 'status'}
          className={`mt-4 rounded-md border px-3 py-2 font-sans text-sm ${
            notice.tone === 'error' ? 'border-error/40 bg-error/5 text-error' : 'border-accent/40 bg-accent/5 text-accent-deep'
          }`}
        >
          {notice.text}
        </p>
      )}

      {/* Balance summary */}
      <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-3">
        <SummaryTile label="Income" value={formatOre(view?.incomeTotalOre ?? 0)} tone="income" />
        <SummaryTile label="Expenses" value={formatOre(view?.expenseTotalOre ?? 0)} tone="expense" />
        <SummaryTile label="Balance" value={formatOre(view?.balanceOre ?? 0)} tone="balance" />
      </div>

      {isBoard && currentYearRow && (
        <div className="mt-4 flex flex-wrap gap-3">
          <button
            type="button"
            onClick={onToggleYear}
            disabled={busy}
            className="inline-flex items-center gap-2 rounded-md border border-border px-3 py-1.5 font-sans text-sm text-ink hover:bg-surface focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent disabled:opacity-50"
          >
            {yearClosed ? <LockOpen aria-hidden className="h-4 w-4" /> : <Lock aria-hidden className="h-4 w-4" />}
            {yearClosed ? `Reopen ${currentYearRow.year}` : `Close ${currentYearRow.year}`}
          </button>
          <button
            type="button"
            onClick={onOpenNextYear}
            disabled={busy}
            className="inline-flex items-center gap-2 rounded-md border border-border px-3 py-1.5 font-sans text-sm text-ink hover:bg-surface focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent disabled:opacity-50"
          >
            <Plus aria-hidden className="h-4 w-4" /> Open next year
          </button>
        </div>
      )}

      {/* Entry form (treasurer) */}
      {isBoard && showForm && (
        <form
          onSubmit={(e) => {
            e.preventDefault();
            void submitEntry();
          }}
          className="mt-6 rounded-lg border border-border bg-surface p-4"
        >
          <h2 className="font-display text-sm font-semibold uppercase tracking-[0.1em] text-ink">
            {editingId ? 'Edit entry' : 'Record an entry'}
          </h2>
          <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
            <label className="space-y-1.5">
              <span className="block font-sans text-sm font-medium text-ink">Amount (EUR)</span>
              <input
                inputMode="decimal"
                value={form.amount}
                onChange={(e) => setForm((f) => ({ ...f, amount: e.target.value }))}
                placeholder="250,00"
                className={inputClass}
              />
            </label>
            <label className="space-y-1.5">
              <span className="block font-sans text-sm font-medium text-ink">Direction</span>
              <select
                value={form.direction}
                onChange={(e) => setForm((f) => ({ ...f, direction: e.target.value as Direction }))}
                className={inputClass}
              >
                <option value="income">Income</option>
                <option value="expense">Expense</option>
              </select>
            </label>
            <label className="space-y-1.5">
              <span className="block font-sans text-sm font-medium text-ink">Date</span>
              <input
                type="date"
                value={form.entryDate}
                onChange={(e) => setForm((f) => ({ ...f, entryDate: e.target.value }))}
                className={inputClass}
              />
            </label>
            <label className="space-y-1.5">
              <span className="block font-sans text-sm font-medium text-ink">Category</span>
              <input
                value={form.category}
                onChange={(e) => setForm((f) => ({ ...f, category: e.target.value }))}
                placeholder="Donations, Venue, …"
                maxLength={80}
                className={inputClass}
              />
            </label>
            <label className="space-y-1.5 sm:col-span-2">
              <span className="block font-sans text-sm font-medium text-ink">Description</span>
              <input
                value={form.description}
                onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
                maxLength={500}
                className={inputClass}
              />
            </label>
            {form.direction === 'expense' && (
              <label className="flex items-center gap-2 sm:col-span-2">
                <input
                  type="checkbox"
                  checked={form.isReimbursement}
                  onChange={(e) => setForm((f) => ({ ...f, isReimbursement: e.target.checked }))}
                  className="h-4 w-4 rounded border-border text-accent focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent"
                />
                <span className="font-sans text-sm text-ink">Documented-cost reimbursement (§19)</span>
              </label>
            )}
          </div>
          <div className="mt-4 flex gap-3">
            <button
              type="submit"
              disabled={busy}
              className="inline-flex items-center gap-2 rounded-md bg-accent px-3.5 py-2 font-sans text-sm font-semibold text-bg hover:bg-accent-deep focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent disabled:opacity-50"
            >
              {busy && <Loader2 aria-hidden className="h-4 w-4 animate-spin" />}
              {editingId ? 'Save changes' : 'Record entry'}
            </button>
            <button
              type="button"
              onClick={() => {
                setShowForm(false);
                setEditingId(null);
              }}
              className="rounded-md border border-border px-3.5 py-2 font-sans text-sm text-ink hover:bg-bg focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent"
            >
              Cancel
            </button>
          </div>
        </form>
      )}

      {/* Ledger */}
      <h2 className="mt-10 font-display text-sm font-semibold uppercase tracking-[0.1em] text-ink">Ledger</h2>
      {!view || view.entries.length === 0 ? (
        <p className="mt-3 rounded-md border border-dashed border-border bg-surface px-4 py-8 text-center font-serif text-muted">
          No entries recorded for {selectedYear}. The books for this year are empty.
        </p>
      ) : (
        <div className="mt-3 overflow-x-auto">
          <table className="w-full border-collapse font-sans text-sm">
            <caption className="sr-only">Ledger entries for {selectedYear}</caption>
            <thead>
              <tr className="border-b border-border text-left text-xs uppercase tracking-wide text-muted">
                <th scope="col" className="py-2 pr-3">Date</th>
                <th scope="col" className="py-2 pr-3">Category</th>
                <th scope="col" className="py-2 pr-3">Description</th>
                <th scope="col" className="py-2 pr-3 text-right">Amount</th>
                {isBoard && <th scope="col" className="py-2 pl-3 text-right">Actions</th>}
              </tr>
            </thead>
            <tbody>
              {view.entries.map((e) => (
                <tr key={e.id} className={`border-b border-border/60 ${e.voided ? 'text-muted line-through' : 'text-ink'}`}>
                  <td className="py-2 pr-3 tabular-nums">{e.entryDate.slice(0, 10)}</td>
                  <td className="py-2 pr-3">
                    {e.category}
                    {e.isReimbursement && !e.voided && (
                      <span className="ml-1.5 rounded bg-accent/10 px-1.5 py-0.5 text-[10px] uppercase tracking-wide text-accent-deep no-underline">
                        reimb.
                      </span>
                    )}
                  </td>
                  <td className="py-2 pr-3">{e.description}</td>
                  <td className={`py-2 pr-3 text-right tabular-nums ${e.direction === 'income' ? 'text-accent-deep' : ''}`}>
                    {e.direction === 'expense' ? '−' : '+'}
                    {formatOre(e.amountOre)}
                  </td>
                  {isBoard && (
                    <td className="py-2 pl-3 text-right">
                      {!e.voided && !yearClosed && (
                        <span className="inline-flex gap-2 no-underline">
                          <button type="button" onClick={() => beginEdit(e.id)} className="text-accent hover:underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent">
                            Edit
                          </button>
                          <button type="button" onClick={() => void onVoid(e.id)} className="text-error hover:underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent">
                            Void
                          </button>
                        </span>
                      )}
                      {e.voided && e.voidReason && <span className="text-xs italic no-underline">voided: {e.voidReason}</span>}
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Category totals + budget-vs-actual */}
      {view && (view.categoryTotals.length > 0 || view.budgetVsActual.length > 0) && (
        <div className="mt-10 grid grid-cols-1 gap-8 lg:grid-cols-2">
          {view.categoryTotals.length > 0 && (
            <div>
              <h2 className="font-display text-sm font-semibold uppercase tracking-[0.1em] text-ink">By category</h2>
              <table className="mt-3 w-full border-collapse font-sans text-sm">
                <thead>
                  <tr className="border-b border-border text-left text-xs uppercase tracking-wide text-muted">
                    <th scope="col" className="py-2 pr-3">Category</th>
                    <th scope="col" className="py-2 pr-3 text-right">Income</th>
                    <th scope="col" className="py-2 text-right">Expense</th>
                  </tr>
                </thead>
                <tbody>
                  {view.categoryTotals.map((c) => (
                    <tr key={c.category} className="border-b border-border/60 text-ink">
                      <td className="py-2 pr-3">{c.category}</td>
                      <td className="py-2 pr-3 text-right tabular-nums">{formatOre(c.incomeOre)}</td>
                      <td className="py-2 text-right tabular-nums">{formatOre(c.expenseOre)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {view.budgetVsActual.length > 0 && (
            <div>
              <h2 className="font-display text-sm font-semibold uppercase tracking-[0.1em] text-ink">Budget vs actual</h2>
              <table className="mt-3 w-full border-collapse font-sans text-sm">
                <thead>
                  <tr className="border-b border-border text-left text-xs uppercase tracking-wide text-muted">
                    <th scope="col" className="py-2 pr-3">Category</th>
                    <th scope="col" className="py-2 pr-3 text-right">Planned</th>
                    <th scope="col" className="py-2 pr-3 text-right">Actual</th>
                    <th scope="col" className="py-2 text-right">Remaining</th>
                  </tr>
                </thead>
                <tbody>
                  {view.budgetVsActual.map((b) => (
                    <tr key={`${b.direction}:${b.category}`} className={`border-b border-border/60 ${b.overspent ? 'text-error' : 'text-ink'}`}>
                      <td className="py-2 pr-3">
                        {b.category} <span className="text-xs text-muted">({b.direction})</span>
                        {b.overspent && (
                          <span className="ml-1.5 rounded bg-error/10 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-error">
                            over
                          </span>
                        )}
                      </td>
                      <td className="py-2 pr-3 text-right tabular-nums">{formatOre(b.plannedOre)}</td>
                      <td className="py-2 pr-3 text-right tabular-nums">{formatOre(b.actualOre)}</td>
                      <td className="py-2 text-right tabular-nums">{formatOre(b.remainingOre)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {isBoard && !yearClosed && <BudgetEditor year={selectedYear} onDone={refresh} setNotice={setNotice} />}
    </section>
  );
}

function SummaryTile({ label, value, tone }: { label: string; value: string; tone: 'income' | 'expense' | 'balance' }) {
  return (
    <div className="rounded-lg border border-border bg-surface px-4 py-5">
      <p className="font-sans text-xs uppercase tracking-[0.15em] text-muted">{label}</p>
      <p className={`mt-1 font-display text-2xl font-semibold tabular-nums ${tone === 'balance' ? 'text-accent-deep' : 'text-ink'}`}>
        {value}
      </p>
    </div>
  );
}

function BudgetEditor({
  year,
  onDone,
  setNotice,
}: {
  year: number | null;
  onDone: () => void | Promise<void>;
  setNotice: (n: Notice) => void;
}) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [b, setB] = useState({ direction: 'expense' as Direction, category: '', planned: '' });

  async function submit() {
    if (busy || year == null) return;
    const plannedOre = parseSekToOre(b.planned);
    if (plannedOre == null) {
      setNotice({ tone: 'error', text: 'Enter a planned amount in euro.' });
      return;
    }
    if (!b.category.trim()) {
      setNotice({ tone: 'error', text: 'A category is required for the budget line.' });
      return;
    }
    setBusy(true);
    const r = await setBudget({ year, direction: b.direction, category: b.category.trim(), plannedOre });
    setBusy(false);
    if (r.kind === 'ok') {
      setNotice({ tone: 'success', text: 'Budget line saved.' });
      setB({ direction: 'expense', category: '', planned: '' });
      setOpen(false);
      void onDone();
    } else if (r.kind === 'conflict') {
      setNotice({ tone: 'error', text: r.message });
    } else {
      setNotice({ tone: 'error', text: 'We couldn’t save the budget line.' });
    }
  }

  return (
    <div className="mt-8">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="font-sans text-sm text-accent hover:underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent"
      >
        {open ? 'Hide budget editor' : 'Set a budget line'}
      </button>
      {open && (
        <form
          onSubmit={(e) => {
            e.preventDefault();
            void submit();
          }}
          className="mt-3 flex flex-wrap items-end gap-3 rounded-lg border border-border bg-surface p-4"
        >
          <select value={b.direction} onChange={(e) => setB((s) => ({ ...s, direction: e.target.value as Direction }))} className={`${inputClass} w-auto`}>
            <option value="income">Income</option>
            <option value="expense">Expense</option>
          </select>
          <input value={b.category} onChange={(e) => setB((s) => ({ ...s, category: e.target.value }))} placeholder="Category" maxLength={80} className={`${inputClass} w-auto`} />
          <input inputMode="decimal" value={b.planned} onChange={(e) => setB((s) => ({ ...s, planned: e.target.value }))} placeholder="Planned (EUR)" className={`${inputClass} w-auto`} />
          <button type="submit" disabled={busy} className="rounded-md bg-accent px-3.5 py-2 font-sans text-sm font-semibold text-bg hover:bg-accent-deep focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent disabled:opacity-50">
            Save line
          </button>
        </form>
      )}
    </div>
  );
}

/** Spec 006 — finances page. Any authenticated member may read the open books (§19). */
export default function Finances() {
  return <RequireRole min="member">{(user) => <FinancesInner user={user} />}</RequireRole>;
}
